import { useState } from "react";
import { UserMinus, Save, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import PageHeader from "../components/PageHeader";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { FieldText, FieldGrade, FieldSection, FieldAcademicYear } from "../components/StudentFormFields";
import { createWithdrawnStudent } from "../lib/api";
import { CURRENT_ACADEMIC_YEAR } from "../lib/constants";

const initial = () => ({
  name: "",
  national_id: "",
  grade: "",
  section: "",
  academic_year: CURRENT_ACADEMIC_YEAR,
  withdrawal_date: new Date().toISOString().slice(0, 10),
});

export default function WithdrawnStudentForm() {
  const [form, setForm] = useState(initial());
  const [loading, setLoading] = useState(false);

  const setField = (k) => (v) => setForm(prev => ({ ...prev, [k]: v, ...(k === "grade" ? { section: "" } : {}) }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.grade || !form.section || !form.academic_year || !form.withdrawal_date) {
      toast.error("الرجاء تعبئة جميع الحقول المطلوبة");
      return;
    }
    if (form.national_id && !/^[0-9]{8,15}$/.test(form.national_id)) {
      toast.error("رقم الهوية يجب أن يكون أرقاماً (8-15 رقم) — أو اتركه فارغاً");
      return;
    }
    setLoading(true);
    try {
      await createWithdrawnStudent(form);
      toast.success("تم تسجيل الطالب المنسحب بنجاح");
      setForm(initial());
    } catch (err) {
      toast.error(err?.response?.data?.detail || "حدث خطأ أثناء الحفظ");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-4xl">
      <PageHeader title="تسجيل طالب منسحب" subtitle="إدخال بيانات انسحاب طالب من المدرسة" icon={UserMinus} />
      <Card className="p-4 sm:p-6 lg:p-8 border-slate-200 shadow-sm">
        <form onSubmit={onSubmit} className="space-y-4 sm:space-y-5" data-testid="withdrawn-student-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-5">
            <FieldText id="w-name" label="اسم الطالب الرباعي" value={form.name} onChange={setField("name")} placeholder="أدخل الاسم كاملاً" required testid="w-input-name" />
            <FieldText id="w-national_id" label="رقم الهوية (اختياري)" value={form.national_id} onChange={setField("national_id")} placeholder="مثال: 1012345678" testid="w-input-national-id" />
            <FieldGrade value={form.grade} onChange={setField("grade")} testid="w-grade-select" />
            <FieldSection grade={form.grade} value={form.section} onChange={setField("section")} testid="w-section-select" />
            <FieldAcademicYear value={form.academic_year} onChange={setField("academic_year")} testid="w-academic-year-select" />
            <FieldText id="withdrawal_date" label="تاريخ الانسحاب" type="date" value={form.withdrawal_date} onChange={setField("withdrawal_date")} required testid="w-input-withdrawal-date" />
          </div>
          <div className="gold-divider opacity-60 my-4" />
          <div className="flex flex-wrap gap-3 justify-end">
            <Button type="button" variant="outline" onClick={() => setForm(initial())} className="h-11 gap-2" data-testid="w-btn-reset">
              <RotateCcw size={16} /> مسح الحقول
            </Button>
            <Button type="submit" disabled={loading} className="h-11 gap-2 bg-rose-600 hover:bg-rose-700 text-white btn-elevate" data-testid="btn-submit-withdrawn">
              <Save size={16} /> {loading ? "جاري الحفظ..." : "حفظ بيانات الانسحاب"}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}
